import { useEffect, useState } from "react";
import { usePostHog } from "@posthog/react";
import { Landmark, ShieldCheck } from "lucide-react";

import {
  captureAuthMfaEnrolled,
  captureAuthMfaRequiredShown,
} from "../../lib/auth-analytics";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  AuthenticatorSetupPanel,
  type AuthenticatorSetupComplete,
} from "./authenticator-setup-panel";

export type PlaidMfaSetupIntent = "connect_bank" | "view_linked_banks";

const INTENT_COPY: Record<
  PlaidMfaSetupIntent,
  { continueLabel: string; description: string; title: string }
> = {
  connect_bank: {
    continueLabel: "Connect bank",
    description:
      "Bank connections require an authenticator app before Plaid can open. Set it up here, then continue the bank connection.",
    title: "Secure this bank connection",
  },
  view_linked_banks: {
    continueLabel: "View linked banks",
    description:
      "Linked bank details require an authenticator app. Set it up here, then continue to your linked-bank list.",
    title: "Secure linked-bank access",
  },
};

export function MfaSetupRequiredDialog({
  intent,
  onAuthenticatorStatusChange,
  onContinue,
  onOpenChange,
}: {
  intent: PlaidMfaSetupIntent | null;
  onAuthenticatorStatusChange?: (enabled: boolean, userId: string) => void;
  onContinue: (intent: PlaidMfaSetupIntent) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const [setupComplete, setSetupComplete] =
    useState<{
      intent: PlaidMfaSetupIntent;
      result: AuthenticatorSetupComplete;
    } | null>(null);
  const posthog = usePostHog();
  const open = intent !== null;

  // Once per open, never per render.
  useEffect(() => {
    if (open) {
      captureAuthMfaRequiredShown(
        (event, props) => posthog?.capture?.(event, props),
        "setup",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copy = intent ? INTENT_COPY[intent] : null;
  const setupCompleteForIntent =
    setupComplete?.intent === intent ? setupComplete.result : null;

  return (
    <Dialog
      open={intent !== null}
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
    >
      {intent && copy ? (
        <DialogContent
          className="max-w-2xl"
          description={copy.description}
          title={copy.title}
        >
          <div className="space-y-5">
            <div className="surface-muted p-4">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                  <Landmark className="h-5 w-5" />
                </span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-foreground">
                    Keep your bank flow in place
                  </p>
                  <p className="text-sm leading-6 text-muted-foreground">
                    Sill will keep this dialog open while you enroll. Once the
                    authenticator app is ready, continue without jumping to a
                    different settings section.
                  </p>
                </div>
              </div>
            </div>

            <AuthenticatorSetupPanel
              codeInputId="plaid-mfa-setup-code"
              onAuthenticatorStatusChange={onAuthenticatorStatusChange}
              onComplete={(result) => {
                if (intent) {
                  captureAuthMfaEnrolled(
                    (event, props) => posthog?.capture?.(event, props),
                    "totp",
                  );
                  setSetupComplete({ intent, result });
                }
              }}
              openingDescription="Confirm in the secure prompt, then Sill will show the QR code here."
              showStartButton={!setupCompleteForIntent}
              startLabel="Set up authenticator app"
              verifyLabel="Verify and continue"
            />

            {setupCompleteForIntent ? (
              <div
                className="rounded-2xl border border-primary/20 bg-primary-soft/50 p-4"
                role="status"
              >
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-1 h-5 w-5 text-primary" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      Authenticator app is ready
                    </p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      Continue the protected Plaid action you started.
                    </p>
                    <div className="mt-4 flex justify-end">
                      <Button onClick={() => onContinue(intent)}>
                        {copy.continueLabel}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
